/**
 * HexMapEvents - Centralized event system for hex-map-app
 *
 * Provides:
 * - Type-safe event creation with consistent bubbles/composed flags
 * - Event type registry with payload definitions
 * - Factory functions for creating properly configured CustomEvents
 *
 * Usage:
 *   import { HexEvents, createHexEvent } from '../utils/hex-events.js';
 *
 *   // Dispatch an event
 *   this.dispatchEvent(createHexEvent(HexEvents.NOTE_SAVED, {
 *     hexCoord: '1,2',
 *     note: noteData,
 *     isEdit: false
 *   }));
 */

/**
 * Event type constants - use these instead of string literals
 * @readonly
 * @enum {string}
 */
export const HexEvents = Object.freeze({
    // Map interaction events
    HEX_CLICKED: 'hex-clicked',
    HEX_RIGHT_CLICKED: 'hex-right-clicked',
    MAP_CLICKED: 'map-clicked',

    // Content events
    NOTE_SAVED: 'note-saved',
    NOTE_DELETED: 'note-deleted',
    ICON_SAVED: 'icon-saved',
    ICON_DELETED: 'icon-deleted',

    // Token events
    TOKEN_CREATED: 'token-created',
    TOKEN_EDITED: 'token-edited',
    TOKEN_MOVED: 'token-moved',
    TOKEN_PLACED: 'token-placed',
    TOKEN_DELETED: 'token-deleted',
    TOKEN_SELECTED: 'token-selected',
    TOKEN_DESELECTED: 'token-deselected',
    CREATE_TOKEN: 'create-token',
    EDIT_TOKEN: 'edit-token',

    // UI events
    PRINT_MODE_SELECTED: 'print-mode-selected',
    DISPLAY_IDS_TOGGLED: 'display-ids-toggled',
    SHOW_TOAST: 'show-toast',
    BACK_TO_MAP: 'back-to-map',

    // Session events
    SESSION_CHANGED: 'session-changed',
    HEX_STATE_CHANGED: 'hex-state-changed',
    READONLY_STATE_CHANGED: 'readonly-state-changed',

    // Calendar events
    CALENDAR_CHANGED: 'calendar-changed',
});

/**
 * Event payload type definitions (for documentation/IDE support)
 * @typedef {Object} HexClickedPayload
 * @property {number} x - Column coordinate
 * @property {number} y - Row coordinate
 * @property {string} coord - Coordinate string "x,y"
 * @property {string} displayId - Display ID (e.g., "0105")
 * @property {Object} [state] - Hex state from session
 */

/**
 * @typedef {Object} NoteSavedPayload
 * @property {string} hexCoord - Coordinate string "x,y"
 * @property {Object} note - Note data object
 * @property {string} note.id - Unique note ID
 * @property {string} note.content - Note text content
 * @property {('show'|'secret')} note.visibility - Visibility setting
 * @property {number} note.createdAt - Timestamp
 * @property {boolean} isEdit - Whether this was an edit vs new note
 */

/**
 * @typedef {Object} IconSavedPayload
 * @property {string} hexCoord - Coordinate string "x,y"
 * @property {Object} icon - Icon data object
 * @property {string} icon.id - Unique icon ID
 * @property {string} icon.iconName - Font Awesome icon name
 * @property {string} icon.color - Hex color code
 * @property {('show'|'secret')} icon.visibility - Visibility setting
 * @property {number} icon.createdAt - Timestamp
 */

/**
 * @typedef {Object} PrintModePayload
 * @property {('gm'|'player')} mode - Print mode selected
 */

/**
 * @typedef {Object} TokenCreatedPayload
 * @property {Object} token - Token data object
 * @property {string} token.id - Unique token ID
 * @property {string} token.name - Token name
 * @property {string} token.color - Token color
 * @property {('show'|'secret')} token.visibility - Visibility setting
 */

/**
 * Create a CustomEvent with proper Shadow DOM configuration
 * Always sets bubbles: true and composed: true for Shadow DOM boundary crossing
 *
 * @param {string} eventType - Event type from HexEvents enum
 * @param {Object} [detail={}] - Event payload data
 * @returns {CustomEvent} Properly configured CustomEvent
 *
 * @example
 * // Simple event with no payload
 * this.dispatchEvent(createHexEvent(HexEvents.MAP_CLICKED));
 *
 * @example
 * // Event with payload
 * this.dispatchEvent(createHexEvent(HexEvents.NOTE_SAVED, {
 *   hexCoord: '1,2',
 *   note: noteData,
 *   isEdit: false
 * }));
 */
export function createHexEvent(eventType, detail = {}) {
    if (!eventType || typeof eventType !== 'string') {
        console.error('createHexEvent: eventType is required and must be a string');
        throw new Error('eventType is required');
    }

    return new CustomEvent(eventType, {
        bubbles: true,
        composed: true,
        detail
    });
}

/**
 * Validate that an event type is a known HexEvent
 * Useful for debugging and development
 *
 * @param {string} eventType - Event type to validate
 * @returns {boolean} True if valid, false otherwise
 */
export function isValidHexEvent(eventType) {
    return Object.values(HexEvents).includes(eventType);
}

/**
 * Get all registered event types
 * Useful for debugging and documentation
 *
 * @returns {string[]} Array of all event type strings
 */
export function getAllEventTypes() {
    return Object.values(HexEvents);
}

// Default export for convenience
export default {
    HexEvents,
    createHexEvent,
    isValidHexEvent,
    getAllEventTypes
};
